import { store } from '../stores/store'
import type { RunStateStore } from '../stores/RunStateStore'
import {
    chameleonTextureNumToStoreString,
    chameleonTextureStringToNum,
    colorStringToRgbUint32,
    decodeNumericShareTuple,
    encodeNumericShareTuple,
    rgbUint32ToHexColor,
} from './sharePayloadCodec'

/**
 * Fixed-order numeric tuple (base64url JSON array), length {@link CARD_SHARE_TUPLE_LENGTH}:
 * remainingHearts → coins → battery (snap) → star (plane) → shield →
 * chameleonColor rgb u32 → chameleonTexture int → recipient (0 = friend, 1 = parent)
 */
export const CARD_SHARE_TUPLE_LENGTH = 8 as const

/**
 * Who a shared card is intended for. Drives conditional visuals on the card
 * landing screen (e.g. the parent headline).
 */
export type CardShareRecipient = 'parent' | 'friend'

const RECIPIENT_PARENT_CODE = 1
const RECIPIENT_FRIEND_CODE = 0

function recipientToNum(recipient: CardShareRecipient): number {
    return recipient === 'parent' ? RECIPIENT_PARENT_CODE : RECIPIENT_FRIEND_CODE
}

function recipientFromNum(n: number): CardShareRecipient {
    return Math.floor(n) === RECIPIENT_PARENT_CODE ? 'parent' : 'friend'
}

/**
 * Decoded card-share payload available on the card landing (`?cID`).
 * Used to drive the collectible-card visuals
 */
export type CardSharePayload = {
    remainingHearts: number
    coinsCollected: number
    batteryPowerUpsCollected: number
    starPowerUpsCollected: number
    shieldPowerUpsCollected: number
    chameleonColor: string
    chameleonTexture: string
    recipient: CardShareRecipient
}

function buildCardShareTuple(
    runState: RunStateStore,
    recipient: CardShareRecipient,
): number[] {
    const stats = runState.snapshot.runEndStats
    return [
        runState.snapshot.lifes,
        stats.coinPowerUpsCollected,
        stats.batteryPowerUpsCollected,
        stats.planePowerUpsCollected,
        stats.shieldPowerUpsCollected,
        colorStringToRgbUint32(store.chameleonColor),
        chameleonTextureStringToNum(store.chameleonTexture),
        recipientToNum(recipient),
    ]
}

function cardShareTupleToPayload(tuple: number[]): CardSharePayload | null {
    const u = tuple

    return {
        remainingHearts: Math.max(0, Math.floor(u[0])),
        coinsCollected: Math.max(0, Math.floor(u[1])),
        batteryPowerUpsCollected: Math.max(0, Math.floor(u[2])),
        starPowerUpsCollected: Math.max(0, Math.floor(u[3])),
        shieldPowerUpsCollected: Math.max(0, Math.floor(u[4])),
        chameleonColor: rgbUint32ToHexColor(u[5]),
        chameleonTexture: chameleonTextureNumToStoreString(u[6]),
        recipient: recipientFromNum(u[7]),
    }
}

export function encodeCardIdQueryValue(
    runState: RunStateStore,
    recipient: CardShareRecipient,
): string {
    return encodeNumericShareTuple(buildCardShareTuple(runState, recipient))
}

// todo: maybe move this to the parents-landing screen
export function applyCardSharePayloadToStore(payload: CardSharePayload): void {
    store.setChameleonColor(payload.chameleonColor)
    store.setChameleonTexture(payload.chameleonTexture)
    store.setParentsCardStats({
        remainingHearts: payload.remainingHearts,
        coinsCollected: payload.coinsCollected,
        batteryPowerUpsCollected: payload.batteryPowerUpsCollected,
        starPowerUpsCollected: payload.starPowerUpsCollected,
        shieldPowerUpsCollected: payload.shieldPowerUpsCollected,
    })
}

export function decodeCardSharePayload(
    cardIdParamValue: string,
): CardSharePayload | null {
    return decodeNumericShareTuple(
        cardIdParamValue,
        CARD_SHARE_TUPLE_LENGTH,
        cardShareTupleToPayload,
    )
}

// Re-export shared color helpers for callers that already import them here.
export { colorStringToRgbUint32, rgbUint32ToHexColor } from './sharePayloadCodec'
